import { useState, useCallback, useMemo } from 'react'
import { Bell } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Badge, Button, ConfirmDialog, Select } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteReadNotifications,
} from '../hooks/useNotifications'
import { useNotificationsView } from '../hooks/useNotificationsView'
import { NotificationList } from './NotificationList'
import { NotificationCompanyTabs } from './NotificationCompanyTabs'
import { NotificationTemplatesMatrix } from './NotificationTemplatesMatrix'
import {
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
} from '../utils/notificationTaxonomy'
import { cn } from '@/lib/cn'

type ViewTab = 'list' | 'templates'

const CARD_CLASSES =
  'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800'

const TYPE_OPTIONS: SelectOption[] = NOTIFICATION_CATEGORIES.map((c) => ({
  value: c.value,
  label: c.label,
}))

export function NotificationsPage(): React.ReactElement {
  const { can } = usePermissions()
  const canManageTemplates = can('settings', 'update')
  const [activeTab, setActiveTab] = useState<ViewTab>('list')
  const [showClearReadConfirm, setShowClearReadConfirm] = useState(false)

  const view = useNotificationsView()
  const { data: unreadCount = 0 } = useUnreadCount()
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const deleteRead = useDeleteReadNotifications()

  const {
    items,
    isLoading,
    error,
    companyTabs,
    allTotal,
    allUnread,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedCategory,
    setSelectedCategory,
    filteredTotal,
    filteredUnread,
    markAllFilter,
    hasMore,
    refetch,
    isLoadingMore,
    loadMore,
  } = view

  const isFiltered = selectedCompanyId !== null || selectedCategory !== ''

  const handleMarkAllRead = useCallback(() => {
    if (filteredUnread === 0) return
    markAllAsRead.mutate(markAllFilter)
  }, [filteredUnread, markAllAsRead, markAllFilter])

  const handleConfirmClearRead = useCallback(() => {
    deleteRead.mutate(undefined)
    setShowClearReadConfirm(false)
  }, [deleteRead])

  const handleClearFilters = useCallback(() => {
    setSelectedCompanyId(null)
    setSelectedCategory('')
  }, [setSelectedCompanyId, setSelectedCategory])

  const emptyLabel = useMemo(() => {
    if (allTotal === 0) return 'No notifications yet.'
    if (isFiltered) return 'No notifications match the selected filters.'
    return 'No notifications.'
  }, [allTotal, isFiltered])

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Organization', to: '/dashboard' },
        { label: 'Notifications' },
      ]}
      icon={Bell}
      title={activeTab === 'list' ? 'My Notifications' : 'Templates'}
      description={
        activeTab === 'list'
          ? 'View and manage your notifications, grouped by company.'
          : 'Configure notification templates for in-app and email.'
      }
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            activeTab === 'list' ? (
              <div className="w-full sm:w-64">
                <Select
                  options={TYPE_OPTIONS}
                  value={selectedCategory}
                  onChange={(v) =>
                    setSelectedCategory(v as NotificationCategory | '')
                  }
                  placeholder="All types"
                  clearable
                />
              </div>
            ) : undefined
          }
          actions={
            activeTab === 'list' ? (
              <>
                {isFiltered && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    Clear filters
                  </Button>
                )}
                {filteredUnread > 0 && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleMarkAllRead}
                    loading={markAllAsRead.isPending}
                  >
                    {isFiltered ? 'Mark filtered as read' : 'Mark all as read'}
                  </Button>
                )}
              </>
            ) : undefined
          }
        />

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex gap-1" aria-label="Section tabs">
            <button
              type="button"
              onClick={() => setActiveTab('list')}
              className={cn(
                'border-b-2 px-4 py-3 text-sm font-medium',
                activeTab === 'list'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
              )}
            >
              My Notifications
              {unreadCount > 0 && (
                <Badge variant="danger" className="ml-2">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </button>
            {canManageTemplates && (
              <button
                type="button"
                onClick={() => setActiveTab('templates')}
                className={cn(
                  'border-b-2 px-4 py-3 text-sm font-medium',
                  activeTab === 'templates'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
                )}
              >
                Templates
              </button>
            )}
          </nav>
        </div>

        {activeTab === 'list' && (
          <div className="space-y-4">
            {/* Company selector */}
            {companyTabs.length > 0 && (
              <div className={cn(CARD_CLASSES, 'p-4 sm:p-5')}>
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Companies
                </p>
                <NotificationCompanyTabs
                  tabs={companyTabs}
                  selectedId={selectedCompanyId}
                  allUnread={allUnread}
                  allTotal={allTotal}
                  onSelect={setSelectedCompanyId}
                />
              </div>
            )}

            <div className={CARD_CLASSES}>
              <div className="p-4 sm:p-6">
                <NotificationList
                  notifications={items}
                  total={filteredTotal}
                  unreadCount={filteredUnread}
                  isLoading={isLoading}
                  error={error}
                  onRetry={refetch}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                  onMarkAsRead={(id, silent) => markAsRead.mutate({ id, silent })}
                  onClearRead={() => setShowClearReadConfirm(true)}
                  isClearingRead={deleteRead.isPending}
                  emptyLabel={emptyLabel}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <section>
            <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">
              Notification templates
            </h2>
            <NotificationTemplatesMatrix />
          </section>
        )}

        <ConfirmDialog
          open={showClearReadConfirm}
          onClose={() => setShowClearReadConfirm(false)}
          onConfirm={handleConfirmClearRead}
          title="Delete read notifications"
          message="Delete all read notifications? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          confirmLoading={deleteRead.isPending}
        />
      </div>
    </PageShell>
  )
}

NotificationsPage.displayName = 'NotificationsPage'
